import React, { useEffect, useRef, useState } from 'react';
import { InputNumber, Radio, Select, Space, Typography } from 'antd';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import type { PlayerMaster } from '@/types';
import { errorsAt } from './dailyEngagementConfig';
import { fromNumber, toNumber, useDraft } from './draftContext';

const { Text } = Typography;

interface Option {
  value: number;
  label: string;
}

const optionFor = (p: PlayerMaster): Option => ({
  value: p.playerIndex,
  label: `${p.displayName || `${p.firstName} ${p.secondName}`.trim()} (#${p.playerIndex})`,
});

/**
 * day28CardId: null = a random Legendary per claim; a player master index =
 * every player gets a Legendary of that player. The server does not check
 * that the player exists; if it can't generate the card, the claim falls back
 * to a random Legendary.
 */
export const Day28CardPicker: React.FC = () => {
  const { draft, editable, errors, update } = useDraft();
  const value = draft.day28CardId;
  const own = errorsAt(errors, 'day28CardId');
  const { data: canSearchData } = useCan({ resource: 'player_data', action: 'list' });
  const canSearch = canSearchData?.can === true;

  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const requestRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const set = (next: number | null) =>
    update((d) => {
      d.day28CardId = next;
    });

  const search = (text: string) => {
    window.clearTimeout(timerRef.current);
    const term = text.trim();
    const direct: Option[] = /^\d+$/.test(term)
      ? [{ value: Number(term), label: `playerIndex ${term}` }]
      : [];
    setOptions(direct);
    if (!term) return;
    timerRef.current = window.setTimeout(() => {
      const request = ++requestRef.current;
      setLoading(true);
      adminApi
        .searchActivePlayerMasters(term)
        .then((res) => {
          if (request !== requestRef.current) return;
          const found = (res.data?.data ?? []).map(optionFor);
          setOptions([...found, ...direct.filter((d) => !found.some((f) => f.value === d.value))]);
          setSearchFailed(false);
        })
        .catch(() => {
          if (request !== requestRef.current) return;
          setSearchFailed(true);
        })
        .finally(() => {
          if (request === requestRef.current) setLoading(false);
        });
    }, 300);
  };

  const shown =
    value !== null && Number.isFinite(value) && !options.some((o) => o.value === value)
      ? [{ value, label: `playerIndex ${value}` }, ...options]
      : options;

  return (
    <Space direction="vertical" size={6}>
      <Radio.Group
        value={value === null ? 'random' : 'player'}
        disabled={!editable}
        onChange={(e) => set(e.target.value === 'random' ? null : NaN)}
      >
        <Radio value="random">Random Legendary (per claim)</Radio>
        <Radio value="player">A specific player</Radio>
      </Radio.Group>
      {value !== null &&
        (canSearch ? (
          <Select<number>
            showSearch
            value={fromNumber(value) ?? undefined}
            placeholder="Search active players by name or playerIndex"
            filterOption={false}
            onSearch={search}
            options={shown}
            loading={loading}
            disabled={!editable}
            status={own.length > 0 ? 'error' : undefined}
            style={{ width: 340 }}
            onChange={(v) => set(v)}
            notFoundContent={loading ? 'Searching…' : 'Type a name or a playerIndex'}
          />
        ) : (
          <InputNumber
            value={fromNumber(value)}
            min={0}
            precision={0}
            placeholder="playerIndex"
            disabled={!editable}
            status={own.length > 0 ? 'error' : undefined}
            style={{ width: 200 }}
            onChange={(v) => set(toNumber(v))}
          />
        ))}
      {value !== null && !canSearch && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Player search needs the player_data:list permission; type the playerIndex instead.
        </Text>
      )}
      {searchFailed && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Player search failed; you can still type a playerIndex.
        </Text>
      )}
      {own.length > 0 && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {own.join('; ')}
        </Text>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        Applies to every Legendary card on a grand milestone day (by default, Day 28&apos;s one
        Legendary).
      </Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        The server does not check the player: if it can&apos;t make that player&apos;s Legendary,
        the claim falls back to a random Legendary.
      </Text>
    </Space>
  );
};
